import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { MediaUpload } from "@/components/MediaUpload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
const TYPES = ["Web Development", "PC Repair", "Automation", "Device Support"];

export default function PortfolioAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items } = useQuery({ queryKey: ["admin-portfolio"], queryFn: () => api.get("/admin/portfolio").then((r) => r.data.items) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-portfolio"] }); qc.invalidateQueries({ queryKey: ["portfolio"] }); };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/portfolio/${p.id}`); refresh(); toast.success("Project deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Portfolio" subtitle="Showcase your best work — updates appear on the site instantly" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setEditing({})} data-testid="new-project-btn"><Plus className="h-4 w-4" /> New Project</Button>
      } />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(items || []).length === 0 ? <p className="text-slate-400 text-sm p-8">No portfolio items yet.</p> :
          (items || []).map((p) => (
            <div key={p.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`project-card-${p.id}`}>
              {p.images?.[0] && <div className="aspect-video bg-slate-100 overflow-hidden"><img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" /></div>}
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-electric">{p.serviceType}</span>
                  {!p.published && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Hidden</span>}
                </div>
                <h3 className="mt-1 font-heading font-semibold text-navy">{p.title}</h3>
                <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.description}</p>
                <div className="flex gap-1 mt-3">
                  <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-project-${p.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-project-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {editing !== null && <ProjectDialog project={editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </div>
  );
}

function ProjectDialog({ project, onClose, onSaved }) {
  const isEdit = !!project.id;
  const [form, setForm] = useState({
    title: project.title || "", description: project.description || "", serviceType: project.serviceType || "Web Development",
    image: project.images?.[0] || null, websiteLink: project.websiteLink || "", clientName: project.clientName || "",
    published: project.published !== false, featured: project.featured !== false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, description: form.description, serviceType: form.serviceType,
      images: form.image ? [form.image] : [], websiteLink: form.websiteLink || null,
      clientName: form.clientName || null, published: form.published, featured: form.featured,
    };
    try {
      if (isEdit) await api.put(`/admin/portfolio/${project.id}`, payload);
      else await api.post("/admin/portfolio", payload);
      toast.success(isEdit ? "Project updated" : "Project created"); onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{isEdit ? "Edit Project" : "New Project"}</DialogTitle><DialogDescription>Add a project image, description, and optional live link.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="project-form">
        <input className={input} placeholder="Project title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="project-title" />
        <select className={input} value={form.serviceType} onChange={(e) => set("serviceType", e.target.value)} data-testid="project-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <textarea className={`${input} h-auto py-2`} rows={3} placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="project-description" />
        <MediaUpload label="Project image" value={form.image} onChange={(v) => set("image", v)} testid="project-image" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Website link (optional)" value={form.websiteLink} onChange={(e) => set("websiteLink", e.target.value)} />
          <input className={input} placeholder="Client name (optional)" value={form.clientName} onChange={(e) => set("clientName", e.target.value)} />
        </div>
        <div className="flex items-center gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="project-published" /> Published</label>
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
        </div>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="project-save">{isEdit ? "Save Changes" : "Create Project"}</Button>
      </form>
    </DialogContent></Dialog>
  );
}
